import { StandardEvents } from '@shopify/events';
import { beginCartLinesUpdate, controllableLineForVariant, variantQuantity } from './standard-cart-events.js';

const CART_ADD_URL = '/cart/add.js';
const CART_CHANGE_URL = '/cart/change.js';
const CART_URL = '/cart.js';
const FEEDBACK_DURATION = 2000;

class CHQuickAddQuantity extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {boolean} */
  #isUpdating = false;
  /** @type {number | undefined} */
  #feedbackTimeout;
  /** @type {number | undefined} */
  #feedbackRaf;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.querySelectorAll('[data-ch-quantity-action]').forEach((button) => {
      button.addEventListener('click', this.#handleClick, { signal });
    });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleStandardCartUpdate, { signal });
    this.#updateState();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    if (this.#feedbackTimeout) window.clearTimeout(this.#feedbackTimeout);
    if (this.#feedbackRaf) window.cancelAnimationFrame(this.#feedbackRaf);
  }

  get #quantity() {
    return Number.parseInt(this.dataset.quantity || '0', 10) || 0;
  }

  set #quantity(value) {
    this.dataset.quantity = String(Math.max(value, 0));
    const valueElement = this.querySelector('[data-ch-quantity-value]');
    if (valueElement) valueElement.textContent = this.dataset.quantity || '0';
    this.#updateState();
  }

  get #step() {
    return Math.max(Number.parseInt(this.dataset.step || '1', 10) || 1, 1);
  }

  get #max() {
    const max = Number.parseInt(this.dataset.max || '', 10);
    return Number.isFinite(max) ? max : null;
  }

  get #variantId() {
    const formVariantInput = this.closest('form')?.querySelector('input[name="id"]');

    if (formVariantInput instanceof HTMLInputElement && formVariantInput.value) {
      return formVariantInput.value;
    }

    return this.dataset.variantId || '';
  }

  #handleClick = async (event) => {
    const button = event.currentTarget;
    if (!(button instanceof HTMLButtonElement) || this.#isUpdating) return;

    const action = button.dataset.chQuantityAction;
    const targetQuantity =
      action === 'increase'
        ? this.#quantity + this.#step
        : Math.max(this.#quantity - this.#step, 0);

    await this.#setQuantity(targetQuantity, action === 'increase');
  };

  #handleCartUpdate = async (event) => {
    const variantId = this.#variantId;
    const cart = event.detail?.resource;
    const isProductFormAdd = event.detail?.data?.source === 'product-form-component';

    if (cart?.items) {
      this.#syncFromCartData(cart, variantId);
    } else if (isProductFormAdd && this.#isCurrentVariantUpdate(event)) {
      this.#quantity = this.#quantity + this.#getEventItemCount(event);
    } else {
      await this.#syncFromCart();
    }

    if (
      this.dataset.regular === 'true' &&
      isProductFormAdd &&
      this.#quantity > 0
    ) {
      this.#hideDuringStandardFeedback();
    }
  };

  /** @param {import('@shopify/events').CartLinesUpdateEvent} event */
  #handleStandardCartUpdate = (event) => {
    const isProductFormAdd = event.action === 'add' && event.detail?.source === 'product-form-component';
    if (isProductFormAdd && this.dataset.regular === 'true') this.#hideDuringStandardFeedback();

    event.promise
      .then(async ({ detail }) => {
        const cart = detail?.ajaxCart || (await this.#fetchCart());
        this.#syncFromCartData(cart, this.#variantId);
      })
      .catch(() => this.#syncFromCart());
  };

  async #setQuantity(targetQuantity, shouldShowFeedback = false) {
    const variantId = this.#variantId;
    if (!variantId || this.dataset.disabled === 'true') return;

    const max = this.#max;
    const nextQuantity = max === null ? targetQuantity : Math.min(targetQuantity, max);

    this.#setUpdating(true);

    try {
      const isAdd = this.#quantity === 0 && nextQuantity > 0;
      if (!isAdd && !this.dataset.lineKey) {
        const currentCart = await this.#fetchCart();
        this.#syncFromCartData(currentCart, variantId);
      }

      const lineKey = this.dataset.lineKey || '';
      if (!isAdd && !lineKey) throw new Error('This product has multiple distinct cart lines and cannot be changed from quick add.');

      const standard = beginCartLinesUpdate(this, {
        action: isAdd ? 'add' : nextQuantity > 0 ? 'update' : 'remove',
        context: 'product',
        lines: isAdd
          ? [{ merchandiseId: variantId, quantity: nextQuantity }]
          : [{ id: lineKey, quantity: nextQuantity }],
        detail: { source: 'ch-quick-add-quantity', productId: this.dataset.productId, variantId },
      });

      let cart;
      try {
        cart = isAdd
          ? await this.#addToCart(variantId, nextQuantity)
          : await this.#changeCart(lineKey, nextQuantity);
        standard.resolve(cart, { itemCount: cart.item_count });
      } catch (error) {
        standard.reject(error);
        throw error;
      }

      this.#syncFromCartData(cart, variantId);
      if (shouldShowFeedback && this.dataset.regular === 'true' && this.#quantity > 0) {
        this.#showStandardFeedback();
      }

    } catch (error) {
      console.error('Failed to update quick add quantity:', error);
      await this.#syncFromCart();
    } finally {
      this.#setUpdating(false);
    }
  }

  async #addToCart(variantId, quantity) {
    const response = await fetch(CART_ADD_URL, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: variantId,
        quantity,
      }),
    });

    if (!response.ok) throw new Error('Unable to add item to cart');

    return this.#fetchCart();
  }

  async #changeCart(lineKey, quantity) {
    const response = await fetch(CART_CHANGE_URL, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: lineKey,
        quantity,
      }),
    });

    if (!response.ok) throw new Error('Unable to change cart quantity');

    return response.json();
  }

  async #syncFromCart() {
    const variantId = this.#variantId;
    if (!variantId) return;

    try {
      const cart = await this.#fetchCart();
      this.#syncFromCartData(cart, variantId);
    } catch (error) {
      console.error('Failed to sync quick add quantity:', error);
    }
  }

  async #fetchCart() {
    const response = await fetch(CART_URL, {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) throw new Error('Unable to fetch cart');

    return response.json();
  }

  #getVariantQuantity(cart, variantId) {
    return variantQuantity(cart, variantId);
  }

  #syncFromCartData(cart, variantId) {
    this.#quantity = this.#getVariantQuantity(cart, variantId);
    const line = controllableLineForVariant(cart, variantId);
    if (line?.key) this.dataset.lineKey = line.key;
    else delete this.dataset.lineKey;
  }

  #isCurrentVariantUpdate(event) {
    const eventVariantId = event.detail?.data?.variantId || event.detail?.sourceId;
    if (eventVariantId) return String(eventVariantId) === String(this.#variantId);

    const eventProductId = event.detail?.data?.productId;
    return eventProductId && String(eventProductId) === String(this.dataset.productId);
  }

  #getEventItemCount(event) {
    const itemCount = Number.parseInt(event.detail?.data?.itemCount || '1', 10);
    return Number.isFinite(itemCount) && itemCount > 0 ? itemCount : 1;
  }

  #setUpdating(isUpdating) {
    this.#isUpdating = isUpdating;
    this.toggleAttribute('aria-busy', isUpdating);
    this.#updateState();
  }

  #updateState() {
    const isDisabled = this.dataset.disabled === 'true';
    const max = this.#max;
    const quantity = this.#quantity;
    const minus = this.querySelector('[data-ch-quantity-action="decrease"]');
    const plus = this.querySelector('[data-ch-quantity-action="increase"]');

    if (minus instanceof HTMLButtonElement) {
      minus.disabled = isDisabled || this.#isUpdating || quantity <= 0;
    }

    if (plus instanceof HTMLButtonElement) {
      plus.disabled = isDisabled || this.#isUpdating || (max !== null && quantity >= max);
    }

    const limitMessage = this.closest('form')?.querySelector('[data-ch-quantity-limit-message]');
    if (limitMessage instanceof HTMLElement) {
      limitMessage.hidden = max === null || quantity < max;
    }

    if (this.dataset.regular === 'true') {
      this.toggleAttribute('data-quantity-active', quantity > 0);
      this.dataset.quantityActive = quantity > 0 ? 'true' : 'false';
    }
  }

  #showStandardFeedback() {
    if (this.#feedbackTimeout) window.clearTimeout(this.#feedbackTimeout);

    const quickAdd = this.closest('quick-add-component');
    if (quickAdd?.classList.contains('quick-add--card-footer')) return;

    const addButton = quickAdd?.querySelector('.quick-add__button');
    if (!(addButton instanceof HTMLButtonElement)) return;

    addButton.classList.add('atc-added');
    this.#hideDuringStandardFeedback();
  }

  #hideDuringStandardFeedback() {
    if (this.#feedbackTimeout) window.clearTimeout(this.#feedbackTimeout);
    if (this.#feedbackRaf) window.cancelAnimationFrame(this.#feedbackRaf);

    this.dataset.feedback = 'true';

    const quickAdd = this.closest('quick-add-component');
    const addButton = quickAdd?.querySelector('.quick-add__button');

    this.#feedbackTimeout = window.setTimeout(() => {
      if (addButton instanceof HTMLButtonElement) {
        addButton.classList.remove('atc-added');
      }
      this.#feedbackTimeout = undefined;
    }, FEEDBACK_DURATION);

    const syncToButton = () => {
      if (!(addButton instanceof HTMLButtonElement) || !addButton.classList.contains('atc-added')) {
        delete this.dataset.feedback;
        this.#feedbackRaf = undefined;
        return;
      }

      this.#feedbackRaf = window.requestAnimationFrame(syncToButton);
    };

    this.#feedbackRaf = window.requestAnimationFrame(syncToButton);
  }
}

if (!customElements.get('ch-quick-add-quantity')) {
  customElements.define('ch-quick-add-quantity', CHQuickAddQuantity);
}
